/**
 * Fast Mark Pipeline Page
 * Batch pipeline: upload paper + memo + responses, confirm the generated
 * rubric, then review the per-learner marking results.
 */

import React, { useState } from 'react';
import {
  Box, Paper, Typography, Button, Alert, CircularProgress,
  Breadcrumbs, Link as MuiLink, Stack, TextField, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Chip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAuthenticatedRequest } from '../../hooks/useAuthenticatedRequest';

interface PipelineRubricCriterion {
  name?: string;
  maxPoints?: number;
}

interface PipelineRubric {
  name: string;
  description?: string;
  criteria: Record<string, PipelineRubricCriterion>;
  total_marks: number;
  marking_preamble?: string | null;
  marking_postamble?: string | null;
}

interface ReviewRow {
  email: string;
  full_name: string;
  learner_id: number | null;
  match_source: string;
  submission_id: number | null;
  score: number | null;
  status: string;
  publishable: boolean;
}

interface GenerateRubricResponse {
  rubric_id: number;
  rubric: PipelineRubric;
}

interface RunPipelineResponse {
  review: ReviewRow[];
}

type Stage = 'upload' | 'rubric' | 'review';

interface FileSlotProps {
  id: string;
  label: string;
  file: File | null;
  onChange: (file: File | null) => void;
  disabled?: boolean;
}

const FileSlot: React.FC<FileSlotProps> = ({ id, label, file, onChange, disabled }) => (
  <Box>
    <Typography variant="subtitle2" gutterBottom>{label}</Typography>
    <input
      type="file"
      id={id}
      onChange={(e) => onChange(e.target.files?.[0] || null)}
      style={{ display: 'none' }}
      disabled={disabled}
    />
    <label htmlFor={id} style={{ cursor: disabled ? 'default' : 'pointer' }}>
      <Button
        component="span"
        variant="outlined"
        startIcon={<CloudUploadIcon />}
        disabled={disabled}
      >
        {file ? file.name : `Choose ${label.toLowerCase()}`}
      </Button>
    </label>
  </Box>
);

const FastMarkPipelinePage: React.FC = () => {
  const navigate = useNavigate();
  const { request } = useAuthenticatedRequest();

  const [stage, setStage] = useState<Stage>('upload');

  // Stage 1 form state
  const [paperFile, setPaperFile] = useState<File | null>(null);
  const [memoFile, setMemoFile] = useState<File | null>(null);
  const [responsesFile, setResponsesFile] = useState<File | null>(null);
  const [assessmentType, setAssessmentType] = useState('');

  // Stage 2 state
  const [rubricId, setRubricId] = useState<number | null>(null);
  const [rubric, setRubric] = useState<PipelineRubric | null>(null);
  const [assignmentId, setAssignmentId] = useState('');

  // Stage 3 state
  const [review, setReview] = useState<ReviewRow[]>([]);

  const [generating, setGenerating] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const extractError = (err: any, fallback: string) =>
    err?.response?.data?.error || fallback;

  const handleGenerateRubric = async () => {
    setError(null);

    if (!paperFile || !memoFile) {
      setError('Please select both the paper and the memo files');
      return;
    }

    setGenerating(true);
    try {
      const formData = new FormData();
      formData.append('action', 'generate_rubric');
      formData.append('paper', paperFile);
      formData.append('memo', memoFile);
      if (assessmentType.trim()) {
        formData.append('assessment_type', assessmentType.trim());
      }

      const response = await request(() => axios.post<GenerateRubricResponse>(
        '/api/fast-mark/pipeline/',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      ));

      setRubricId(response.data.rubric_id);
      setRubric(response.data.rubric);
      setStage('rubric');
    } catch (err: any) {
      console.error('Error generating rubric:', err);
      setError(extractError(err, 'Failed to generate rubric. Please try again.'));
    } finally {
      setGenerating(false);
    }
  };

  const handleRunPipeline = async () => {
    setError(null);

    if (!responsesFile) {
      setError('Please select a responses file');
      return;
    }
    if (!assignmentId.trim() || Number.isNaN(Number(assignmentId))) {
      setError('Please enter a numeric assignment ID');
      return;
    }
    if (rubricId === null) {
      setError('No rubric available. Please generate a rubric first.');
      return;
    }

    setRunning(true);
    try {
      const formData = new FormData();
      formData.append('action', 'run');
      formData.append('responses', responsesFile);
      formData.append('assignment_id', assignmentId.trim());
      formData.append('rubric_id', rubricId.toString());

      const response = await request(() => axios.post<RunPipelineResponse>(
        '/api/fast-mark/pipeline/',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 }
      ));

      setReview(response.data.review || []);
      setStage('review');
    } catch (err: any) {
      console.error('Error running pipeline:', err);
      setError(extractError(err, 'Failed to run the marking pipeline. Please try again.'));
    } finally {
      setRunning(false);
    }
  };

  const handleRegenerate = () => {
    setError(null);
    setRubricId(null);
    setRubric(null);
    setReview([]);
    setStage('upload');
  };

  const criteriaRows = rubric ? Object.entries(rubric.criteria || {}) : [];

  return (
    <Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <MuiLink
          component="button"
          variant="body1"
          onClick={() => navigate('/dashboard')}
          sx={{ cursor: 'pointer', textDecoration: 'none' }}
        >
          Dashboard
        </MuiLink>
        <MuiLink
          component="button"
          variant="body1"
          onClick={() => navigate('/fast-mark')}
          sx={{ cursor: 'pointer', textDecoration: 'none' }}
        >
          Fast Mark
        </MuiLink>
        <Typography color="text.primary">Batch Pipeline</Typography>
      </Breadcrumbs>

      <Box mb={3}>
        <Typography variant="h4" gutterBottom>Batch Pipeline</Typography>
        <Typography variant="body2" color="textSecondary">
          Generate a rubric from a paper and memo, then mark a batch of responses
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      {stage === 'upload' && (
        <Paper sx={{ p: 4 }}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                1. Upload assessment files
              </Typography>
              <Stack spacing={2}>
                <FileSlot
                  id="pipeline-paper-upload"
                  label="Paper"
                  file={paperFile}
                  onChange={setPaperFile}
                  disabled={generating}
                />
                <FileSlot
                  id="pipeline-memo-upload"
                  label="Memo"
                  file={memoFile}
                  onChange={setMemoFile}
                  disabled={generating}
                />
                <FileSlot
                  id="pipeline-responses-upload"
                  label="Responses"
                  file={responsesFile}
                  onChange={setResponsesFile}
                  disabled={generating}
                />
                <TextField
                  label="Assessment type"
                  value={assessmentType}
                  onChange={(e) => setAssessmentType(e.target.value)}
                  disabled={generating}
                  fullWidth
                  helperText="Optional, e.g. Mathematics Paper 1"
                />
              </Stack>
            </Box>

            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={handleGenerateRubric}
                disabled={generating || !paperFile || !memoFile}
                startIcon={generating ? <CircularProgress size={20} /> : <CheckCircleIcon />}
              >
                {generating ? 'Generating...' : 'Generate rubric'}
              </Button>
            </Box>
          </Stack>
        </Paper>
      )}

      {stage === 'rubric' && rubric && (
        <Paper sx={{ p: 4 }}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                2. Confirm the generated rubric
              </Typography>
              <Typography variant="h6">{rubric.name}</Typography>
              {rubric.description && (
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {rubric.description}
                </Typography>
              )}
              <Typography variant="body2" fontWeight="bold" sx={{ mt: 1 }}>
                Total marks: {rubric.total_marks}
              </Typography>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Question</TableCell>
                    <TableCell>Criterion</TableCell>
                    <TableCell align="right">Max points</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {criteriaRows.map(([question, criterion]) => (
                    <TableRow key={question}>
                      <TableCell>{question}</TableCell>
                      <TableCell>{criterion.name || '—'}</TableCell>
                      <TableCell align="right">{criterion.maxPoints ?? 0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TextField
              label="Assignment ID"
              value={assignmentId}
              onChange={(e) => setAssignmentId(e.target.value)}
              type="number"
              required
              disabled={running}
              sx={{ maxWidth: 240 }}
            />

            {!responsesFile && (
              <Alert severity="warning">
                No responses file selected. Use "Regenerate" to go back and upload one.
              </Alert>
            )}

            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={handleRegenerate} disabled={running}>
                Regenerate
              </Button>
              <Button
                variant="contained"
                onClick={handleRunPipeline}
                disabled={running || !responsesFile || !assignmentId.trim()}
                startIcon={running ? <CircularProgress size={20} /> : <CheckCircleIcon />}
              >
                {running ? 'Marking...' : 'Continue to marking'}
              </Button>
            </Box>
          </Stack>
        </Paper>
      )}

      {stage === 'review' && (
        <Paper sx={{ p: 4 }}>
          <Stack spacing={3}>
            <Typography variant="subtitle1" fontWeight="bold">
              3. Review results
            </Typography>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Match source</TableCell>
                    <TableCell align="right">Score</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Publishable</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {review.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="textSecondary">
                          No results returned
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {review.map((row, index) => (
                    <TableRow key={`${row.email}-${index}`}>
                      <TableCell>{row.full_name}</TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{row.match_source}</TableCell>
                      <TableCell align="right">{row.score ?? '—'}</TableCell>
                      <TableCell>{row.status}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.publishable ? 'Yes' : 'No'}
                          color={row.publishable ? 'success' : 'default'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box display="flex" justifyContent="flex-end">
              <Button variant="contained" onClick={() => navigate('/fast-mark')}>
                Done
              </Button>
            </Box>
          </Stack>
        </Paper>
      )}
    </Box>
  );
};

export default FastMarkPipelinePage;
